import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { LandingLayout } from '@/pages/landing/shared/LandingLayout';
import { AdminLayout } from '@/layouts/admin-layout';
import { useAuth } from '@/contexts/auth-context';
import { Link, useSearch } from 'wouter';
import { liveIndustries, tradeName } from '@/marketing/live';
import { PricingTable, type PricingOffer } from '@/components/subscription/PricingTable';
import { subscriptionService } from '@/services/api/subscription.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Mail } from 'lucide-react';

import { SITE_URL, SOCIAL_IMAGE_URL } from '@/lib/brand';
import { ANNUAL_MONTHS, PLANS } from '@/marketing/plans';
import { pricingSoftwareSchema } from '@/marketing/pricing-schema';
import { SUPPORT_EMAIL } from '@/config/brand';
import { FUNNEL_OFFER_ON, discountedCents, hasOffer, offerRegisterHref, rememberOffer } from '@/marketing/funnel-offer';
import { FUNNEL_COPY } from '@/marketing/funnel-copy';

// Prices are in US dollars everywhere (owner decision 2026-10-05): no IP, timezone or language detection.
const CURRENCY = 'usd';

// The billing FAQ: shown on the page and written into the FAQPage JSON-LD from this one list, so the two cannot differ. Seats and the annual
// months are read from src/marketing/plans.data.json (through PLANS), never typed into the translations.
const FAQ_KEYS = ['whichPlan', 'billingCycle', 'changePlans', 'trialEnd', 'cancel', 'payment'] as const;
const seatsOf = (code: string) => PLANS.find((p) => p.code === code)?.seats;
const FAQ_VALUES = { solo: seatsOf('solo'), crew: seatsOf('crew'), team: seatsOf('team'), months: ANNUAL_MONTHS, free: 12 - ANNUAL_MONTHS };

export default function PricingPage() {
  const industries = liveIndustries();
  const { t } = useTranslation(['billing', 'home']);
  const { user } = useAuth();
  // the ad offer's banner: only for exactly /pricing?offer=<the public offer id>, and only in a build with the offer switch on (any other value is ignored)
  const search = useSearch();
  const offerBanner = FUNNEL_OFFER_ON && hasOffer(search);
  // the plan buttons go to /register and the register page sends the offer to the server: it waits in sessionStorage and rides in the link (when storage is unavailable)
  useEffect(() => {
    if (offerBanner) rememberOffer();
  }, [offerBanner]);

  // the plan cards' share of the offer: the prices are computed from the ones the cards show
  const cardOffer: PricingOffer | undefined = offerBanner ? { discount: discountedCents, copy: FUNNEL_COPY.pricing } : undefined;

  // Fetch current subscription if user is logged in
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
    enabled: !!user,
  });

  const hasActiveSubscription = subscriptionData?.subscription?.status === 'active';

  // Choose layout based on auth status
  const Layout = user ? AdminLayout : LandingLayout;

  const pricingContent = (
    <div className={user ? '' : 'min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800'}>
      <Helmet>
        <title>{t('pricing.seoTitle')}</title>
        <meta name="title" content={t('pricing.seoTitle')} />
        <meta name="description" content={t('pricing.seoDescription')} />
        <link rel="canonical" href={`${SITE_URL}/pricing`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/pricing`} />
        <meta property="og:title" content={t('pricing.seoTitle')} />
        <meta property="og:description" content={t('pricing.seoDescription')} />
        <meta property="og:image" content={SOCIAL_IMAGE_URL} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pricing.seoTitle')} />
        <meta name="twitter:description" content={t('pricing.seoDescription')} />

        {/* Structured Data - Product Offers (prices from the same plans.data.json as the table) */}
        <script type="application/ld+json">
          {JSON.stringify(pricingSoftwareSchema())}
        </script>

        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQ_KEYS.map((key) => ({
              "@type": "Question",
              "name": t(`pricing.faq.${key}.question`, FAQ_VALUES),
              "acceptedAnswer": {
                "@type": "Answer",
                "text": t(`pricing.faq.${key}.answer`, FAQ_VALUES)
              }
            }))
          })}
        </script>
      </Helmet>

      <div className={user ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16'}>
          {offerBanner && (
            <p data-offer-banner className="mx-auto mb-6 max-w-2xl rounded-xl border-2 border-gray-900 bg-white px-4 py-3 text-center text-base font-semibold text-gray-900 sm:text-lg">
              {FUNNEL_COPY.banner}
            </p>
          )}
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className={`${user ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl'} font-bold text-gray-900 dark:text-white mb-3 sm:mb-4`}>
              {t('home:pricing.sectionTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-4">
              {t('home:pricing.sectionSubtitle')}
            </p>
          </div>

          {/* Pricing Table for all users */}
          {user && hasActiveSubscription ? (
            <Card className="max-w-2xl mx-auto mb-12">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  {t('pricing.activeSubscription.title')}
                </CardTitle>
                <CardDescription>
                  {t('pricing.activeSubscription.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button asChild>
                    <a href="/admin/billing">
                      {t('pricing.activeSubscription.manageBilling')}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <PricingTable
              currency={CURRENCY}
              interval="month"
              className="mb-12"
              headingLevel={2}
              registerHref={offerBanner ? offerRegisterHref() : undefined}
              offer={cardOffer}
            />
          )}
          {!user && industries.length > 0 && (
            <nav aria-label="Industries using Scopie" className="-mt-6 mb-12 text-center text-sm text-gray-600">
              Using Scopie for{' '}
              {industries.map((p, i) => (
                <span key={p.slug}>
                  {i > 0 && (i === industries.length - 1 ? ' or ' : ', ')}
                  <Link href={p.path} className="font-medium text-brand-600 hover:underline">{tradeName(p).toLowerCase()}</Link>
                </span>
              ))}
              ? See how it works.
            </nav>
          )}

          {/* What the plans include (facts: llms.txt, the terms, plans.data.json) */}
          <section className={`${user ? 'mt-10 sm:mt-12' : 'mt-16'} text-center`}>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
              {t('pricing.included.title')}
            </h2>
            <div className="max-w-3xl mx-auto space-y-3 text-base sm:text-lg text-gray-600 dark:text-gray-300">
              <p>{t('pricing.included.intro', FAQ_VALUES)}</p>
              <p>{t('pricing.included.flow')}</p>
            </div>
          </section>

          {/* FAQ Section */}
          <div className={`${user ? 'mt-10 sm:mt-12' : 'mt-16'} text-center`}>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
              {t('pricing.faq.title')}
            </h2>
            <div className="max-w-3xl mx-auto space-y-5 sm:space-y-6 text-left">
              {FAQ_KEYS.map((key) => (
                <div key={key} className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white mb-2">
                    {t(`pricing.faq.${key}.question`, FAQ_VALUES)}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    {t(`pricing.faq.${key}.answer`, FAQ_VALUES)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className={`${user ? 'mt-10 sm:mt-12 pb-4' : 'mt-16'} text-center`}>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-4">
              {t('pricing.helpChoosing')}
            </p>
            <Button
              variant="outline"
              size="lg"
              className="shadow-sm hover:shadow-md transition-shadow inline-flex items-center gap-2"
              onClick={() => window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Pricing Inquiry`}
            >
              <Mail className="w-4 h-4" />
              {t('pricing.contactSales')}
            </Button>
          </div>
        </div>
      </div>
  );

  return <Layout>{pricingContent}</Layout>;
}
